'use client'

import { motion } from 'framer-motion'
import { FileText, Scale, AlertTriangle, Shield, Users, Globe } from 'lucide-react'

const sections = [
  {
    title: 'Acceptance of Terms',
    icon: FileText,
    content: [
      'By accessing and using the Elyndra Studios website, you accept and agree to be bound by these Terms of Service.',
      'If you do not agree to these terms, please do not use our website or services.',
      'We reserve the right to modify these terms at any time, and your continued use constitutes acceptance of any changes.'
    ]
  },
  {
    title: 'Use of Our Website',
    icon: Globe,
    content: [
      'You may use our website for lawful purposes only and in accordance with these Terms of Service.',
      'You agree not to use the website in any way that could damage, disable, or impair our services.',
      'You must not attempt to gain unauthorized access to our systems or networks.',
      'Any content you submit must be accurate, lawful, and not infringe on third-party rights.'
    ]
  },
  {
    title: 'Intellectual Property',
    icon: Shield,
    content: [
      'All content on this website, including text, graphics, logos, and software, is owned by Elyndra Studios Ltd.',
      'You may not reproduce, distribute, or create derivative works without our written permission.',
      'Our trademarks and service marks may not be used without our prior written consent.',
      'We respect intellectual property rights and expect users to do the same.'
    ]
  },
  {
    title: 'Product Information',
    icon: Users,
    content: [
      'Information about our products (Sparky Ventures and RebelVerify) is provided for informational purposes only.',
      'As our products are currently in development, features and availability may change without notice.',
      'We make no warranties about the accuracy or completeness of product information.',
      'Final product specifications may differ from those described on our website.'
    ]
  },
  {
    title: 'Limitation of Liability',
    icon: AlertTriangle,
    content: [
      'Elyndra Studios Ltd shall not be liable for any indirect, incidental, or consequential damages.',
      'Our total liability shall not exceed the amount paid by you for our services, if any.',
      'We provide our website and information "as is" without warranties of any kind.',
      'We do not guarantee uninterrupted or error-free operation of our website.'
    ]
  },
  {
    title: 'Governing Law',
    icon: Scale,
    content: [
      'These Terms of Service are governed by the laws of England and Wales.',
      'Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.',
      'If any provision of these terms is found to be unenforceable, the remaining provisions shall remain in effect.',
      'These terms constitute the entire agreement between you and Elyndra Studios Ltd.'
    ]
  }
]

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-white">
      <main>
        {/* Hero Section */}
        <section className="pt-24 pb-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Terms of <span className="gradient-text">Service</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                These terms govern your use of the Elyndra Studios website and services. 
                Please read them carefully before using our platform.
              </p>
              <div className="mt-8 text-sm text-gray-500">
                Last updated: March 7, 2025
              </div>
            </motion.div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="container mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold mb-6">Legal Framework</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                These Terms of Service establish the legal framework for your use of our website 
                and services. As an early-stage technology company developing innovative EdTech 
                and privacy solutions, we want to ensure clear expectations for all users.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Terms Sections */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="space-y-12">
              {sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <section.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800">{section.title}</h3>
                  </div>
                  <ul className="space-y-3">
                    {section.content.map((item, itemIndex) => (
                      <li key={itemIndex} className="text-gray-600 leading-relaxed flex items-start gap-3">
                        <span className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 px-4 bg-gradient-to-br from-primary-50 to-secondary-50">
          <div className="container mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Questions About Our Terms?</h3>
                <p className="text-lg mb-6 opacity-90">
                  If you have any questions about these Terms of Service or need clarification 
                  on any provisions, please contact us.
                </p>
                <div className="space-y-2 mb-6">
                  <p className="text-lg">Email: legal@elyndrastudios.com</p>
                  <p className="text-lg">General: hello@elyndrastudios.com</p>
                </div>
                <motion.a
                  href="/#contact"
                  className="bg-white text-primary-600 font-semibold py-3 px-8 rounded-2xl hover:bg-gray-50 transition-colors inline-flex items-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Contact Us
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </div>
  )
}
